import { useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Grid3X3, Check } from 'lucide-react';

export type CareModeResourceType = 'schedule' | 'narrative' | 'choices';

interface CareModeViewerProps {
  type: CareModeResourceType;
  title: string;
  // Schedule
  schedule?: any;
  currentStepIndex?: number;
  onStepChange?: (idx: number) => void;
  completedStepIds?: Set<string>;
  onToggleStep?: (stepKey: string) => void;
  stepChoices?: Record<string, { image: string; label: string }>;
  onOpenLinkedChoiceBoard?: (boardId: string, stepKey: string) => void;
  // Narrative
  narrative?: any;
  currentPageIndex?: number;
  onPageChange?: (idx: number) => void;
  // Choice board
  choiceBoard?: any;
  choiceItems?: any[];
  loadingChoiceItems?: boolean;
  selectedChoiceItemIds?: number[];
  onChoiceSelect?: (id: number) => void;
  onConfirmChoices?: () => void;
  confirmationSent?: boolean;
  needsConfirmation?: boolean;
  // Common
  onClose: () => void;
}

export function CareModeViewer(props: CareModeViewerProps) {
  const {
    type, title, onClose,
    schedule, currentStepIndex = 0, onStepChange, completedStepIds, onToggleStep, stepChoices, onOpenLinkedChoiceBoard,
    narrative, currentPageIndex = 0, onPageChange,
    choiceBoard, choiceItems = [], loadingChoiceItems, selectedChoiceItemIds = [], onChoiceSelect, onConfirmChoices, confirmationSent, needsConfirmation,
  } = props;

  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  // Lock body scroll while care mode is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Compute current item and totals
  let total = 0;
  let currentIdx = 0;
  let image = '';
  let caption = '';
  let stepLabel = '';

  if (type === 'schedule' && schedule?.steps) {
    total = schedule.steps.length;
    currentIdx = currentStepIndex;
    const step = schedule.steps[currentStepIndex];
    const stepKey = step?.id || `step-${currentStepIndex}`;
    const choiceOverride = stepChoices?.[String(stepKey)];
    image = choiceOverride?.image || step?.image || '';
    stepLabel = step?.label || step?.title || '';
    caption = step?.notes || '';
  } else if (type === 'narrative' && narrative?.pages) {
    total = narrative.pages.length;
    currentIdx = currentPageIndex;
    const page = narrative.pages[currentPageIndex];
    image = page?.image || '';
    caption = page?.text || '';
  }

  const canGoBack = currentIdx > 0;
  const canGoNext = type !== 'choices' && currentIdx < total - 1;

  const handlePrev = () => {
    if (type === 'schedule' && onStepChange) onStepChange(Math.max(0, currentIdx - 1));
    else if (type === 'narrative' && onPageChange) onPageChange(Math.max(0, currentIdx - 1));
  };

  const handleNext = () => {
    if (type === 'schedule' && onStepChange) onStepChange(Math.min(total - 1, currentIdx + 1));
    else if (type === 'narrative' && onPageChange) onPageChange(Math.min(total - 1, currentIdx + 1));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
      if (dx < 0) handleNext();
      else handlePrev();
    }
  };

  // Schedule step details
  const currentStep = type === 'schedule' ? schedule?.steps?.[currentStepIndex] : null;
  const stepKey = currentStep ? String(currentStep.id || `step-${currentStepIndex}`) : '';
  const isCompleted = completedStepIds?.has(stepKey) ?? false;
  const choiceLabel = stepChoices?.[stepKey]?.label;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-white select-none"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: 'pan-y' }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-blue-600 text-white flex-shrink-0">
        <div className="flex flex-col">
          <span className="text-sm font-medium opacity-80">
            {type === 'choices' ? 'Make a Choice' : `Step ${currentIdx + 1} of ${total}`}
          </span>
          <span className="text-lg font-bold leading-tight line-clamp-1">{title}</span>
        </div>
        <button
          onClick={onClose}
          className="w-11 h-11 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          aria-label="Close Care Mode"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Progress bar (schedule / narrative) */}
      {type !== 'choices' && total > 0 && (
        <div className="h-1.5 bg-blue-100 flex-shrink-0">
          <div
            className="h-full bg-blue-500 transition-all duration-300"
            style={{ width: `${((currentIdx + 1) / total) * 100}%` }}
          />
        </div>
      )}

      {/* ─── SCHEDULE / NARRATIVE content ─── */}
      {(type === 'schedule' || type === 'narrative') && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Image area */}
          <div className="flex-1 relative bg-gray-50 overflow-hidden">
            {image ? (
              <img
                src={image}
                alt={stepLabel || caption || 'Resource image'}
                className="absolute inset-0 w-full h-full object-contain"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-gray-300">
                <div className="text-center">
                  <div className="text-8xl mb-4">🖼️</div>
                  <p className="text-xl text-gray-400">No image</p>
                </div>
              </div>
            )}

            {/* Step label overlay (schedule) */}
            {type === 'schedule' && stepLabel && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-6 py-4">
                <p className="text-white text-2xl font-bold text-center">{stepLabel}</p>
                {choiceLabel && (
                  <p className="text-blue-200 text-lg text-center mt-1">Chosen: {choiceLabel}</p>
                )}
              </div>
            )}

            {/* Complete indicator (schedule) */}
            {type === 'schedule' && isCompleted && (
              <div className="absolute top-4 right-4 bg-green-500 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg">
                <Check className="w-7 h-7" />
              </div>
            )}
          </div>

          {/* Caption (narrative) or notes (schedule) */}
          {caption && (
            <div className="flex-shrink-0 bg-blue-50 border-t border-blue-100 px-6 py-4 max-h-40 overflow-y-auto">
              <p className="text-xl text-gray-800 text-center leading-relaxed">{caption}</p>
            </div>
          )}

          {/* Action row: mark complete + linked board */}
          {type === 'schedule' && currentStep && (
            <div className="flex-shrink-0 flex items-center justify-center gap-4 px-6 py-3 bg-white border-t border-gray-100">
              {onToggleStep && (
                <button
                  onClick={() => onToggleStep(stepKey)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full text-lg font-semibold transition-colors ${
                    isCompleted
                      ? 'bg-green-100 text-green-700 border-2 border-green-400'
                      : 'bg-gray-100 text-gray-600 border-2 border-gray-300 hover:border-green-400 hover:text-green-600'
                  }`}
                  style={{ minHeight: 52 }}
                >
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isCompleted ? 'bg-green-500 border-green-500' : 'border-gray-400'}`}>
                    {isCompleted && <Check className="w-4 h-4 text-white" />}
                  </div>
                  {isCompleted ? 'Done!' : 'Mark Done'}
                </button>
              )}
              {currentStep.linkedChoiceBoardId && !isCompleted && !choiceLabel && onOpenLinkedChoiceBoard && (
                <button
                  onClick={() => onOpenLinkedChoiceBoard(currentStep.linkedChoiceBoardId, stepKey)}
                  className="flex items-center gap-2 px-6 py-3 rounded-full text-lg font-semibold bg-purple-100 text-purple-700 border-2 border-purple-300 hover:bg-purple-200 transition-colors"
                  style={{ minHeight: 52 }}
                >
                  <Grid3X3 className="w-5 h-5" />
                  Open Choices
                </button>
              )}
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex-shrink-0 flex gap-4 px-6 py-5 bg-white border-t border-gray-200">
            <button
              onClick={handlePrev}
              disabled={!canGoBack}
              className="flex-1 flex items-center justify-center gap-3 rounded-2xl text-2xl font-bold transition-all"
              style={{
                minHeight: 72,
                background: canGoBack ? '#1d4ed8' : '#e5e7eb',
                color: canGoBack ? '#ffffff' : '#9ca3af',
                fontSize: '1.5rem',
              }}
              aria-label="Previous"
            >
              <ChevronLeft className="w-9 h-9" />
              <span>Back</span>
            </button>
            <button
              onClick={handleNext}
              disabled={!canGoNext}
              className="flex-1 flex items-center justify-center gap-3 rounded-2xl text-2xl font-bold transition-all"
              style={{
                minHeight: 72,
                background: canGoNext ? '#1d4ed8' : '#e5e7eb',
                color: canGoNext ? '#ffffff' : '#9ca3af',
                fontSize: '1.5rem',
              }}
              aria-label="Next"
            >
              <span>Next</span>
              <ChevronRight className="w-9 h-9" />
            </button>
          </div>

          {/* All done banner */}
          {type === 'schedule' && completedStepIds && schedule?.steps &&
            completedStepIds.size === schedule.steps.length && schedule.steps.length > 0 && (
            <div className="flex-shrink-0 bg-green-500 text-white text-center py-4 text-2xl font-bold">
              🎉 All done! Great job!
            </div>
          )}
        </div>
      )}

      {/* ─── CHOICE BOARD content ─── */}
      {type === 'choices' && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {loadingChoiceItems ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent" />
            </div>
          ) : (
            <>
              {/* Description */}
              {choiceBoard?.description && (
                <div className="flex-shrink-0 px-6 py-3 bg-blue-50 border-b border-blue-100">
                  <p className="text-lg text-gray-700 text-center">{choiceBoard.description}</p>
                </div>
              )}

              {/* Choice grid */}
              <div className="flex-1 overflow-y-auto p-4">
                {confirmationSent ? (
                  <div className="h-full flex flex-col items-center justify-center gap-4">
                    <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center">
                      <Check className="w-14 h-14 text-green-600" />
                    </div>
                    <p className="text-3xl font-bold text-green-700 text-center">Choice sent!</p>
                  </div>
                ) : (
                  <div className={`grid gap-4 ${choiceItems.length <= 4 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                    {choiceItems.map((item: any) => {
                      const isSelected = selectedChoiceItemIds.includes(item.id);
                      return (
                        <button
                          key={item.id}
                          onClick={() => onChoiceSelect?.(item.id)}
                          className={`relative rounded-2xl border-4 overflow-hidden transition-all flex flex-col items-center ${
                            isSelected
                              ? 'border-blue-600 shadow-lg scale-95 bg-blue-50'
                              : 'border-gray-200 bg-white hover:border-blue-300'
                          }`}
                          style={{ minHeight: 140 }}
                        >
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={item.label} className="w-full aspect-square object-cover" />
                          ) : (
                            <div className="w-full aspect-square bg-gray-100 flex items-center justify-center text-5xl">
                              🖼️
                            </div>
                          )}
                          <span className="px-2 py-2 text-center text-base font-semibold text-gray-800 w-full">
                            {item.label}
                          </span>
                          {isSelected && (
                            <div className="absolute top-2 right-2 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                              <Check className="w-5 h-5 text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Confirm button */}
              {!confirmationSent && needsConfirmation && selectedChoiceItemIds.length > 0 && onConfirmChoices && (
                <div className="flex-shrink-0 px-6 py-5 bg-white border-t border-gray-200">
                  <button
                    onClick={onConfirmChoices}
                    className="w-full rounded-2xl bg-blue-600 text-white text-2xl font-bold flex items-center justify-center gap-3"
                    style={{ minHeight: 72 }}
                  >
                    <Check className="w-8 h-8" />
                    Confirm Choice
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
